<script setup lang="ts">
/** API-first. The snippets target the real base URL and the OpenAI-compatible chat route. */
import { ref } from 'vue'
import Icon from '~/components/ui/Icon.vue'

const tab = ref<'python' | 'node' | 'curl'>('python')
const tabs = ['python', 'node', 'curl'] as const
</script>

<template>
  <section id="developers" class="relative overflow-hidden border-b border-border">
    <div class="pointer-events-none absolute -bottom-48 left-1/2 size-[var(--measure-720px)] -translate-x-1/2 rounded-full glow-cyan" />
    <div class="relative mx-auto grid max-w-[var(--measure-1120px)] items-center gap-10 px-6 py-16 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <div class="flex flex-col gap-4">
        <span class="num text-[length:var(--type-fine)] font-semibold text-info">API-first</span>
        <h2 class="m-0 text-[length:var(--measure-2rem)] font-bold leading-[1.1] tracking-[-0.025em] text-pretty">Point the OpenAI SDK at your endpoint.</h2>
        <p class="m-0 max-w-[var(--measure-400px)] text-[length:var(--measure-1rem)] leading-relaxed text-muted-foreground text-pretty">Streaming, usage and citations come back in the shapes you already handle. Keys are scoped to the organisation; every deployment has revisions you can roll back.</p>
        <ul class="m-0 flex list-none flex-col gap-2 p-0 text-[length:var(--measure-12p5px)]">
          <li class="flex items-center gap-2 text-muted-foreground"><Icon name="check" :size="12" class="text-primary" />chat/completions · predictions</li>
          <li class="flex items-center gap-2 text-muted-foreground"><Icon name="check" :size="12" class="text-primary" />API keys per organisation, metered requests</li>
          <li class="flex items-center gap-2 text-muted-foreground"><Icon name="check" :size="12" class="text-primary" />export-manifest: take the weights with you</li>
        </ul>
      </div>

      <div class="overflow-hidden rounded-[var(--measure-10px)] border border-border bg-background shadow-window">
        <div class="flex h-9 items-center border-b border-border px-1">
          <button
v-for="t in tabs" :key="t" type="button" class="num h-9 border-b-2 px-3 text-[length:var(--type-meta)]"
                  :class="tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'" @click="tab = t">{{ t }}</button>
          <span class="num ml-auto pr-3 text-[length:var(--type-fine)] text-muted-foreground">studio.scalty.com/api/v1</span><TodoBadge />
        </div>
        <pre v-if="tab === 'python'" class="num m-0 overflow-x-auto p-4 text-[length:var(--measure-0p8571rem)] leading-[1.7] text-foreground/85"><span class="text-info">from</span> openai <span class="text-info">import</span> OpenAI

client = OpenAI(
  base_url=<span class="text-primary">"https://studio.scalty.com/api/v1"</span>,
  api_key=<span class="text-primary">"sk_live_…"</span>,
)

r = client.chat.completions.create(
  model=<span class="text-primary">"support-assistant-prod"</span>,
  messages=[{<span class="text-primary">"role"</span>: <span class="text-primary">"user"</span>, <span class="text-primary">"content"</span>: <span class="text-primary">"Hi"</span>}],
)</pre>
        <pre v-else-if="tab === 'node'" class="num m-0 overflow-x-auto p-4 text-[length:var(--measure-0p8571rem)] leading-[1.7] text-foreground/85"><span class="text-info">import</span> OpenAI <span class="text-info">from</span> <span class="text-primary">"openai"</span>;

<span class="text-info">const</span> client = <span class="text-info">new</span> OpenAI({
  baseURL: <span class="text-primary">"https://studio.scalty.com/api/v1"</span>,
  apiKey: <span class="text-primary">"sk_live_…"</span>,
});

<span class="text-info">const</span> r = <span class="text-info">await</span> client.chat.completions.create({
  model: <span class="text-primary">"support-assistant-prod"</span>,
  messages: [{ role: <span class="text-primary">"user"</span>, content: <span class="text-primary">"Hi"</span> }],
});</pre>
        <pre v-else class="num m-0 overflow-x-auto p-4 text-[length:var(--measure-0p8571rem)] leading-[1.7] text-foreground/85"><span class="text-info">curl</span> https://studio.scalty.com/api/v1/chat/completions \
  -H <span class="text-primary">"Authorization: Bearer sk_live_…"</span> \
  -H <span class="text-primary">"Content-Type: application/json"</span> \
  -d <span class="text-primary">'{"model":"support-assistant-prod",
       "messages":[{"role":"user","content":"Hi"}]}'</span></pre>
        <div class="num flex items-center gap-3 border-t border-border bg-card px-4 py-2.5 text-[length:var(--type-fine)] text-muted-foreground">
          <span class="text-primary">200</span><span>stream · 41 tokens</span><span>first token 180ms</span>
          <span class="ml-auto flex items-center gap-1.5"><span class="size-1.5 rounded-full bg-primary shadow-glow-primary" />support-assistant-prod · v6</span>
        </div>
      </div>
    </div>
  </section>
</template>
